
import { useState, useEffect, useRef } from 'react';
import { bookData } from '../../../mocks/en/bookData';
import { createClient } from '@supabase/supabase-js';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Policy {
  id: string;
  name: string;
  icon: string;
  content: string;
  last_updated: string;
  status: string;
  language: string;
}

interface FooterProps {
  onOpenPolicy?: (policyId: string) => void;
}

export default function Footer({ onOpenPolicy }: FooterProps) {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedPolicy, setSelectedPolicy] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const pendingPolicyType = useRef<'privacy' | 'cookies' | null>(null);
  const footerReveal = useScrollReveal({ threshold: 0.1 });

  useEffect(() => {
    loadPolicies();
  }, []);

  // When policies are loaded, check if there's a pending policy to open
  useEffect(() => {
    if (!loading && policies.length > 0 && pendingPolicyType.current) {
      const policyNames = {
        privacy: 'Privacy Policy',
        cookies: 'Cookie Policy'
      };
      const policy = policies.find(p => p.name === policyNames[pendingPolicyType.current!]);
      if (policy) {
        openPolicy(policy.id);
      }
      pendingPolicyType.current = null;
    }
  }, [loading, policies]);

  // Listen for policy open events from CookieConsent
  useEffect(() => {
    const handleOpenPolicy = (event: Event) => {
      const customEvent = event as CustomEvent<{ policyType: 'privacy' | 'cookies' }>;
      const { policyType } = customEvent.detail;
      const policyNames = {
        privacy: 'Privacy Policy',
        cookies: 'Cookie Policy'
      };
      
      if (loading || policies.length === 0) {
        // Store the pending policy type to open when loaded
        pendingPolicyType.current = policyType;
        return;
      }
      
      const policy = policies.find(p => p.name === policyNames[policyType]);
      if (policy) {
        openPolicy(policy.id);
      }
    };

    window.addEventListener('openPolicy', handleOpenPolicy);
    return () => {
      window.removeEventListener('openPolicy', handleOpenPolicy);
    };
  }, [policies, loading]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('policies')
        .select('*')
        .eq('language', 'en')
        .order('name', { ascending: true });

      if (error) throw error;
      setPolicies(data || []);
    } catch (error) {
      console.error('Error loading policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
    }
  };

  const openPolicy = (policyId: string) => {
    setSelectedPolicy(policyId);
    if (onOpenPolicy) {
      onOpenPolicy(policyId);
    }
  };

  const closePolicy = () => {
    setSelectedPolicy(null);
  };

  // Expose openPolicyByName function globally for CookieConsent
  useEffect(() => {
    (window as any).openPolicyByName = (policyType: 'privacy' | 'cookies') => {
      const policyNames = {
        privacy: 'Privacy Policy',
        cookies: 'Cookie Policy'
      };
      const policy = policies.find(p => p.name === policyNames[policyType]);
      if (policy) {
        openPolicy(policy.id);
      }
    };
    
    return () => {
      delete (window as any).openPolicyByName;
    };
  }, [policies]);

  const currentPolicy = policies.find(p => p.id === selectedPolicy);

  return (
    <>
      <footer 
        ref={footerReveal.ref}
        className={`bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-t-3xl ${
          footerReveal.isVisible ? 'animate-fade-in-up' : 'opacity-0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Left column */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4 group">
                <img src={bookData.logoImage} alt="Logo" className="h-10 w-10 object-contain group-hover:scale-110 transition-transform duration-300" />
                <span className="font-serif text-xl font-bold">THE FOUR GATES WITHIN</span>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Practical guide on how to understand yourself and live an authentic life.
              </p>
              
              {/* Newsletter */}
              <form onSubmit={handleSubscribe} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="w-full bg-transparent border-b-2 border-gray-600 focus:border-white py-2 outline-none transition-all duration-300 text-base placeholder-gray-500 hover:border-gray-400"
                  required
                />
                <button
                  type="submit"
                  className="bg-white text-gray-900 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer group hover:scale-105 hover:shadow-lg"
                >
                  SUBSCRIBE
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </button>
                {subscribeStatus === 'success' && (
                  <p className="text-green-400 text-sm animate-fade-in-up">Thank you for subscribing!</p>
                )}
              </form>
            </div>

            {/* Middle column - Policies */}
            <div>
              <h3 className="text-sm font-semibold tracking-wider uppercase text-gray-400 mb-4">
                POLICIES
              </h3>
              {loading ? (
                <div className="text-gray-500 text-sm">Loading...</div>
              ) : (
                <ul className="space-y-3">
                  {policies.map((policy) => (
                    <li key={policy.id}>
                      <button
                        onClick={() => openPolicy(policy.id)}
                        className="text-white hover:text-amber-400 transition-all duration-300 underline cursor-pointer text-left relative group"
                      >
                        <span className="relative">
                          {policy.name}
                          {/* Underline slide animation */}
                          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-400 group-hover:w-full transition-all duration-300"></span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Right column - Contact */}
            <div>
              <h3 className="text-sm font-semibold tracking-wider uppercase text-gray-400 mb-4">
                CONTACT
              </h3>
              <div className="space-y-3 mb-6">
                <a 
                  href="mailto:giedrius@giedriuspetrauskas.com" 
                  className="text-white hover:text-amber-400 transition-all duration-300 underline block relative group cursor-pointer"
                >
                  <span className="relative">
                    giedrius@giedriuspetrauskas.com
                    {/* Underline slide animation */}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-400 group-hover:w-full transition-all duration-300"></span>
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-gray-400">
              <p>© 2026 All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Policy Modal */}
      {selectedPolicy && currentPolicy && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in-up">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-2xl animate-scale-in">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${currentPolicy.icon} text-2xl text-purple-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{currentPolicy.name}</h3>
              </div>
              <button
                onClick={closePolicy}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-all duration-300 cursor-pointer group"
              >
                <i className="ri-close-line text-2xl text-gray-600 group-hover:rotate-90 transition-transform duration-300"></i>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
              {currentPolicy.content ? (
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{currentPolicy.content}</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="ri-file-text-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">This policy content has not been uploaded yet.</p>
                  <p className="text-sm text-gray-400 mt-2">Administrator can upload content in admin panel.</p>
                </div>
              )}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Last updated: {new Date(currentPolicy.last_updated).toLocaleDateString('en-US')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
